import { BackendSocket } from '../../hooks/use-socket';
import { SubscriberContext } from '../../hooks/use-backend';
import { BingoWinnerMessage } from '../../types/bingo-events/in/backend/winner-message';
import { meta, winners, current, ticketWins } from '../../stores/backend';
import { isValidPart, withCurrentSession } from './session';
import { mergeConfirmedWins } from '../../lib/confirmed-wins';

export const onWinnerMessage = (
    socket: BackendSocket,
    { payload, postBalanceChange }: SubscriberContext
) => {
    const callback = (data: BingoWinnerMessage) =>
        withCurrentSession('bingo:winnerMessage', data?.gameSessionId, () => {
            const parts = Object.keys(current.game_parts.value).length;

            const validWinners = (
                Array.isArray(data.winners) ? data.winners : []
            ).filter(
                (winner) =>
                    winner != null &&
                    isValidPart(winner.part) &&
                    (typeof winner.cardId === 'number' ||
                        typeof winner.cardId === 'string') &&
                    winner.userId != null
            );
            const consolationWinners = Array.isArray(data.consolationWinners)
                ? data.consolationWinners
                : [];
            const sameSession =
                ticketWins.gameSessionId.value === data.gameSessionId;
            // Read plain snapshots: retaining Hookstate proxies here and writing
            // them back into their own store creates self-referential proxies
            // that recurse infinitely on the next read.
            const previousRecords = sameSession
                ? ticketWins.records.get({ noproxy: true })
                : [];
            const previousWinners = winners.winners.get({ noproxy: true });
            const previousConsolationWinners = winners.consolationWinners.get({
                noproxy: true,
            });
            const complete =
                (sameSession && ticketWins.complete.value) ||
                validWinners.some((winner) => winner.part === parts) ||
                consolationWinners.length > 0;
            const records = mergeConfirmedWins(previousRecords, validWinners);
            const dataWinners = [
                ...validWinners,
                ...(Array.isArray(data.extraWinners) ? data.extraWinners : []),
            ];
            const presentationWinners = mergeConfirmedWins(
                previousWinners,
                dataWinners
            );
            const presentationConsolation = [
                ...new Map(
                    [...previousConsolationWinners, ...consolationWinners].map(
                        (winner) => [JSON.stringify(winner), winner]
                    )
                ).values(),
            ];
            if (
                sameSession &&
                complete === ticketWins.complete.value &&
                JSON.stringify(records) === JSON.stringify(previousRecords) &&
                JSON.stringify(presentationWinners) ===
                    JSON.stringify(previousWinners) &&
                JSON.stringify(presentationConsolation) ===
                    JSON.stringify(previousConsolationWinners)
            )
                return;

            ticketWins.set({
                gameSessionId: data.gameSessionId,
                records,
                complete,
            });

            payload.getAccountStatus();
            postBalanceChange('local-action');
            winners.gameSessionId.set(data.gameSessionId);
            winners.winners.set(presentationWinners);
            winners.consolationWinners.set(presentationConsolation);

            const latestWinnerPart = validWinners.reduce(
                (result, winner) => Math.max(result, winner.part),
                current.current_part.value + 1
            );
            current.current_part.set(latestWinnerPart - 1);

            meta.modals.winner.set(
                current.game_continued.value === true || complete
            );
        });

    socket.on('bingo:winnerMessage', callback);

    return () => {
        socket.off('bingo:winnerMessage', callback);
    };
};
